<script setup>
import { defineProps, computed, ref, nextTick, onMounted, onBeforeUnmount, watch } from "vue";
import { useNewsFeedStore } from "@/store/NewsFeed/newsFeed";
import { t } from "@/controllerLK/GlobusLocalization";
import ArrowRightIcon from "@/components/Icons/ArrowRightIcon.vue";
import IconSortObjects from "@/components/Icons/IconSortObjects.vue";

const newsFeedStore = useNewsFeedStore();

const props = defineProps({
  isOpenList: {
    type: Boolean,
    required: true
  }
});

onMounted(() => {
  isTeleportReady.value = !!document.querySelector("#containerNews");
});

onBeforeUnmount(() => {
  // Очищаем состояние списка объектов перед размонтированием
  hideList();
  cancelHide();
});

// Функция сортировки объектов аналогично городам.
const getSortedObjects = (objectType) => {
  // Получаем все объекты из initialFacetObjects в зависимости от типа
  let allObjects = [];

  switch (objectType) {
    case "persons":
      allObjects = newsFeedStore.initialFacetObjects.persons || [];
      break;
    case "locations":
      allObjects = newsFeedStore.initialFacetObjects.locations || [];
      break;
    case "organisations":
      allObjects = newsFeedStore.initialFacetObjects.organisations || [];
      break;
    default:
      allObjects = [];
  }

  if (newsFeedStore.filterObject.condition) {
    // Получаем доступные объекты из facetParams в зависимости от типа
    let availableObjects = [];

    switch (objectType) {
      case "persons":
        availableObjects = newsFeedStore.facetParams?.persons || [];
        break;
      case "locations":
        availableObjects = newsFeedStore.facetParams?.locations || [];
        break;
      case "organisations":
        availableObjects = newsFeedStore.facetParams?.organisations || [];
        break;
      default:
        availableObjects = [];
    }

    // Создаем Set для быстрого поиска доступных объектов
    const availableSet = new Set(availableObjects);

    // Разделяем на доступные и недоступные
    const availableItems = allObjects.filter(item => availableSet.has(item));
    const unavailableItems = allObjects.filter(item => !availableSet.has(item));

    // Сортируем каждую группу по имени
    availableItems.sort((a, b) => a.localeCompare(b));
    unavailableItems.sort((a, b) => a.localeCompare(b));

    nextTick();

    // Возвращаем сначала доступные, затем недоступные
    return [...availableItems, ...unavailableItems];
  } else {
    // Если условие false, возвращаем исходный список
    nextTick();
    return allObjects;
  }
};

const facetObjects = computed(() => {
  return [
    {
      title: t("settings_item.object_list.persons"),
      items: getSortedObjects("persons")
    },
    // {
    //   title: t('settings_item.object_list.locations'),
    //   items: getSortedObjects("locations")
    // },
    {
      title: t("settings_item.object_list.organisations"),
      items: getSortedObjects("organisations")
    }
  ];
});

// Функция проверки доступности объекта для выбора
const isObjectDisabled = (item, objectType) => {
  if (newsFeedStore.filterObject.condition) {
    let availableObjects = [];

    switch (objectType) {
      case t("settings_item.object_list.persons"):
        availableObjects = newsFeedStore.facetParams?.persons || [];
        break;
      case t("settings_item.object_list.locations"):
        availableObjects = newsFeedStore.facetParams?.locations || [];
        break;
      case t("settings_item.object_list.organisations"):
        availableObjects = newsFeedStore.facetParams?.organisations || [];
        break;
      default:
        return false;
    }

    return !!newsFeedStore.facetParams && !availableObjects?.find(obj => obj === item);
  } else {
    return false;
  }
};

// Флаг запрета выбора объектов во время загрузки.
// Для предотвращения выбора выключенных городов в параметре "И"
const isLoading = ref(false);

const addObjectInFilter = async (item, objectType) => {
  if (isObjectDisabled(item, activeObjectType.value) || isLoading.value) return;
  isLoading.value = true;
  // Тип объекта.
  const filterKey = switchType(objectType);

  await newsFeedStore.toggleObjectInFilter(item, filterKey);
  await newsFeedStore.clearNewsFeed();
  await newsFeedStore.getNews();
  isLoading.value = false;
};

// Количество выбранных объектов
const getSelectedObjectCount = (objectType) => {
  const filterKey = switchType(objectType);
  return newsFeedStore.filterObject.listID[filterKey].length;
};

// Переключение отображения типа объектов
const switchType = (objectType) => {
  let filterKey;
  switch (objectType) {
    case t("settings_item.object_list.persons"):
      filterKey = "persons";
      break;
    case t("settings_item.object_list.locations"):
      filterKey = "locations";
      break;
    case t("settings_item.object_list.organisations"):
      filterKey = "organisations";
      break;
    default:
      return;
  }
  return filterKey;
};

// Значение поисковой строки
const searchQuery = ref("");

// Флаг отображения модального окна выбора объектов.
const isShowList = ref(false);

// Активный тип объекта
const activeObjectType = ref("");

// ID Активного типа объекта. Используется для отображения выбранных объектов.
// const activeObjectTypeID = computed(() => {
//   let filterKey = switchType(activeObjectType.value);
//   let id = null;
//   if (filterKey === "persons") id = 0;
//   if (filterKey === "locations") id = 1;
//   if (filterKey === "organisations") id = 2;
//   return id;
// });

// Computed для получения items активного типа объекта
const activeItems = computed(() => {
  if (!activeObjectType.value) return [];

  const found = facetObjects.value.find(obj => obj.title === activeObjectType.value);
  return found ? found.items : [];
});

// Дополнительно. Фильтруем по поисковому запросу и сортируем:
// Выбранные в начало списка, остальные после.
const sortedAndFilteredItems = computed(() => {
  // Сначала фильтруем по поисковому запросу
  const filtered = activeItems.value
    .filter((filterItem) => filterItem.toLowerCase().includes(searchQuery.value.toLowerCase()));

  // Разделяем на выбранные и невыбранные
  const selected = filtered.filter(item =>
    newsFeedStore.filterObject.list.some(obj => obj === item)
  );
  const notSelected = filtered.filter(item =>
    !newsFeedStore.filterObject.list.some(obj => obj === item)
  );

  // Возвращаем: сначала выбранные, затем остальные
  return [...selected, ...notSelected];
});

// Отображение окна объектов

// Активный таймер скрытия окна объектов
const hideTimeout = ref(null);
// Время через которое окно будет скрыто, после ухода курсора
const HIDE_DELAY = 500; // мс

// Запланированное скрытие окна, для возможности наведения мыши
const scheduleHide = () => {
  console.log("shedule hide start");
  // Очищаем предыдущий таймер, если есть
  if (hideTimeout.value) {
    clearTimeout(hideTimeout.value);
  }

  hideTimeout.value = setTimeout(() => {
    hideList();
  }, HIDE_DELAY);
};

// Отмена скрытия объектов с очисткой таймера.
const cancelHide = () => {
  if (hideTimeout.value) {
    clearTimeout(hideTimeout.value);
    hideTimeout.value = null;
  }
};

// Функция. Отображение списка объектов
const showList = (objectType) => {
  cancelHide(); // Отменяем запланированное скрытие

  if (objectType !== activeObjectType.value) {
    activeObjectType.value = objectType;
    isShowList.value = true;
  }
  searchQuery.value = "";
};

// Функция. Скрытие списка объектов
const hideList = () => {
  isShowList.value = false;
  activeObjectType.value = "";
  searchQuery.value = "";
};

// Существование элемента #containerNews
const isTeleportReady = ref(false);

// Фильтрация.
const sortingType = ref("asc");
const sortingTypeNameList = {
  asc: t("settings_item_objects.sort.asc"),
  popular: t("settings_item_objects.sort.popular")
};

const isShowSort = ref(false);
const toggleShowSort = () => {
  // Если закрываем меню, отменяем таймер
  if (isShowSort.value) {
    cancelHide();
  }
  isShowSort.value = !isShowSort.value;
};

// Отслеживаем закрытие меню сортировки
watch(isShowSort, (newValue) => {
  if (!newValue) {
    // Меню закрылось - отменяем все таймеры
    cancelHide();
  }
});
</script>

<template>
  <div
    v-if="props.isOpenList"
    class="settings-object scroll"
    @mouseenter="cancelHide"
    @mouseleave="scheduleHide"
  >
    <ul class="settings-object__list">
      <li
        v-for="objectType in facetObjects"
        :key="objectType.title"
        class="settings-object__item"
        :class="{'settings-object__item--active': objectType.title === activeObjectType}"
        @mouseenter="showList(objectType.title)"
      >
        <span class="settings-object__item-title">
          {{ objectType.title }}
          <span
            v-if="getSelectedObjectCount(objectType.title) > 0"
            class="settings-object__item-count"
          >
            {{ getSelectedObjectCount(objectType.title) }}
          </span>
        </span>
        <ArrowRightIcon
          class="settings-object__item-icon"
          :class="{'settings-object__item-icon--active': objectType.title === activeObjectType}"
        />
      </li>
    </ul>

    <!--  Окно выбора объектов.  -->

    <Teleport
      v-if="isTeleportReady"
      to="#containerNews"
    >
      <div
        v-if="isShowList"
        class="object-view"
        @mouseenter="cancelHide"
        @mouseleave="scheduleHide"
      >
        <div class="object-view__header">
          <InputSearchSecondary
            v-model="searchQuery"
            :placeholder="t('labels.search')"
          />
          <ButtonFixedFilled
            :pressed="isShowSort"
            :icon-only="true"
            @click="toggleShowSort"
          >
            <IconSortObjects
              class="sorting__icon"
              :class="{'sorting__icon--active': isShowSort}"
            />
          </ButtonFixedFilled>
          <div
            v-if="isShowSort"
            class="sorting"
            @mouseenter="cancelHide"
            @mouseleave="scheduleHide"
          >
            <ListView>
              <ListItem
                v-for="(value, key) in sortingTypeNameList"
                :key="key"
                v-model="sortingType"
                :item="key"
                @click="isShowSort = false; cancelHide();"
              >
                {{ value }}
              </ListItem>
            </ListView>
          </div>
        </div>
        <ListView class="object-view__list scroll">
          <ListItem
            v-for="item in sortedAndFilteredItems"
            :key="item"
            :item="item"
            :model-value="newsFeedStore.filterObject.list"
            :disabled="isObjectDisabled(item, activeObjectType)"
            :use-checkbox="true"
            @update:model-value="addObjectInFilter(item, activeObjectType)"
          >
            {{ item }}
          </ListItem>
        </ListView>
      </div>
    </Teleport>
  </div>
</template>

<style scoped lang="scss">
.sorting {
  position: absolute;
  background-color: var(--background-bg-white);
  padding: 6px;
  border-radius: 8px;
  box-shadow: 1px 2px 10px 0 #131E2726;
  top: calc(100% + 10px);
  right: 0;
  z-index: 999;
  min-width: 180px;

  &__icon {
    &--active {
      color: var(--icon-icon-on);
    }
  }
}

.settings-object {
  display: flex;
  flex-direction: column;
  max-height: 300px;
  gap: 10px;

  &__list {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 14px 0 14px;
    border-radius: 8px;
    height: 30px;
    cursor: pointer;

    &-title {
      display: flex;
      gap: 6px;
      align-items: center;
      text-transform: uppercase;
      font-size: 12px;
      font-weight: 400;
      color: var(--text-text-pur);
    }

    &-count {
      font-size: 12px;
      font-weight: 400;
      color: var(--text-text-on);
    }

    &:hover:not(&--active) {
      background: var(--background-bg-hover);
    }

    &--active {
      background-color: var(--background-bg-active-hover);
    }

    &-icon {
      width: 14px;
      height: 14px;
      color: var(--icon-icon-primary);

      &--active {
        transform: rotate(180deg);
        color: var(--icon-icon-on);
      }
    }
  }
}

.object-view {
  display: flex;
  flex-direction: column;
  gap: 10px;
  position: absolute;
  left: 230px;
  top: 0;
  background-color: var(--background-bg-white);
  box-shadow: 1px 2px 10px 0 var(--gray-gray50);
  height: 100%;
  width: 222px;
  border-radius: 8px;
  padding: 10px;
  z-index: 10;
  animation: slideIn 0.15s ease-out;

  &__header {
    display: flex;
    gap: 5px;
    position: relative;
  }

  &__list {
    padding-right: 4px !important;

    &-selected {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      border-bottom: 1px solid var(--border-bd-divider);
      padding-bottom: 10px;
      max-height: 200px;
      flex-shrink: 0;
    }
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
</style>